import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { GANTT_STATUSES, ganttMeta, statusLabel } from "./utils/bookingStatus";
import { buildTimeline, centreColour, indiesByCampus, indieSpan, isoDay, sparklinePath, weekStarts } from "./utils/groupsTimeline";
import { getBookingGantt } from "../BookingGantt/api/bookingGanttApi";
import { shortDate } from "../BookingGantt/utils/ganttLayout";
import IndependentStudentsDialog from "../BookingGantt/IndependentStudentsDialog";

// Timeline view of the Booking Groups list. A lighter planning picture than
// the Gantt: a week scale, one colour per centre, a pill per group with its
// head count and dates, status as a dot, and the independent students as a
// dashed "virtual group" with a sparkline of how many are on site.
// Click a group to edit it; click the independent students to list them.

const LABEL_W = 180;
// Nights stretch to fill the width, but never narrower than this: a long
// season scrolls rather than squashing the pills.
const MIN_DAY_W = 16;
const LANE_H = 44;
const PILL_H = 34;

function counts(g) {
  const s = Array.isArray(g.studentIds) ? g.studentIds.length : 0;
  const a = Array.isArray(g.adultIds) ? g.adultIds.length : 0;
  return `${s} ${s === 1 ? "student" : "students"} · ${a} ${a === 1 ? "adult" : "adults"}`;
}

function dateRange(start, end) {
  const s = isoDay(start);
  const e = isoDay(end);
  return `${s ? shortDate(s) : "?"} – ${e ? shortDate(e) : "?"}`;
}

export default function GroupsTimeline({ groups, selectedYear, onOpenGroup, onSelectStudent }) {
  const [gantt, setGantt] = useState(null);
  const [indiesFor, setIndiesFor] = useState(null);

  // Independent students come from the Gantt endpoint; without it the view
  // still shows the groups.
  useEffect(() => {
    let cancelled = false;
    const year = Number(selectedYear) || new Date().getFullYear();
    getBookingGantt({ year })
      .then((d) => !cancelled && setGantt(d))
      .catch(() => !cancelled && setGantt(null));
    return () => { cancelled = true; };
  }, [selectedYear]);

  const indies = useMemo(() => indiesByCampus(gantt), [gantt]);
  const { dates, sections, undated } = useMemo(() => buildTimeline(groups, indies), [groups, indies]);
  const weeks = useMemo(() => weekStarts(dates), [dates]);
  // Fill the available width: measure the scroll box and share it between the nights.
  const scrollRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => setBoxWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [dates.length]);
  const dayW = dates.length ? Math.max(MIN_DAY_W, Math.floor(((boxWidth - LABEL_W) / dates.length) * 100) / 100) : MIN_DAY_W;
  const width = dates.length * dayW;
  const today = new Date().toISOString().slice(0, 10);
  const todayIndex = dates.indexOf(today);

  if (!dates.length) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="text.secondary">No dated groups to show. Groups need a start and end date to appear here.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, fontFamily: (t) => t.typography.fontFamily, textAlign: "left" }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1 }} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Status</Typography>
        {GANTT_STATUSES.map((s) => (
          <Chip key={s.key} size="small" variant="outlined" label={s.label}
            icon={<Box component="span" sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: s.color, border: "1px solid rgba(0,0,0,0.3)", ml: "6px !important" }} />} />
        ))}
        <Chip size="small" variant="outlined" label="Cancelled" sx={{ textDecoration: "line-through", color: "text.disabled" }} />
      </Stack>

      <Box ref={scrollRef} sx={{ overflow: "auto", maxHeight: "calc(100vh - 260px)", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
        <Box sx={{ position: "relative", width: LABEL_W + width, minWidth: LABEL_W + width }}>
          {/* week scale */}
          <Box sx={{ display: "flex", position: "sticky", top: 0, zIndex: 3, bgcolor: "background.paper", borderBottom: "1px solid", borderColor: "divider", height: 30 }}>
            <Box sx={{ position: "sticky", left: 0, zIndex: 4, width: LABEL_W, minWidth: LABEL_W, bgcolor: "background.paper" }} />
            <Box sx={{ position: "relative", width, flex: "none" }}>
              {weeks.map((w) => (
                <Typography key={w.date} variant="caption" color="text.secondary"
                  sx={{ position: "absolute", left: w.index * dayW + 4, top: 7, whiteSpace: "nowrap" }}>
                  w/c {shortDate(w.date)}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* alternating week bands and today, behind the lanes */}
          <Box sx={{ position: "absolute", top: 30, bottom: 0, left: LABEL_W, width, pointerEvents: "none" }}>
            {weeks.map((w, i) => (i % 2 ? (
              <Box key={w.date} sx={{ position: "absolute", top: 0, bottom: 0, left: w.index * dayW, width: 7 * dayW, bgcolor: "action.hover" }} />
            ) : null))}
            {todayIndex >= 0 ? (
              <Box sx={{ position: "absolute", top: 0, bottom: 0, left: todayIndex * dayW, borderLeft: "2px solid", borderColor: "error.main", opacity: 0.6 }} />
            ) : null}
          </Box>

          {sections.map((sec, si) => {
            const accent = centreColour(si);
            const lanes = sec.bars.length + (sec.indies ? 1 : 0);
            return (
              <Box key={sec.campus} sx={{ display: "flex", borderBottom: "1px solid", borderColor: "divider" }}>
                {/* the centre, once, spanning its lanes */}
                <Box sx={{
                  position: "sticky", left: 0, zIndex: 2, width: LABEL_W, minWidth: LABEL_W,
                  bgcolor: "background.paper", borderRight: "1px solid", borderColor: "divider",
                  borderLeft: `4px solid ${accent}`, px: 1.5, py: 1,
                }}>
                  <Typography variant="subtitle2" sx={{ color: accent, fontWeight: 700 }}>{sec.campus}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sec.bars.length} {sec.bars.length === 1 ? "group" : "groups"}
                    {sec.indies ? ` · up to ${sec.indies.peak} independent` : ""}
                  </Typography>
                </Box>

                <Box sx={{ position: "relative", width, flex: "none", height: lanes * LANE_H + 8 }}>
                  {sec.bars.map((b, li) => {
                    const g = b.group;
                    const meta = b.cancelled ? null : ganttMeta(b.colorKey);
                    return (
                      <Tooltip key={g.id} title={`${g.groupName || `Group ${g.id}`} · ${statusLabel(g.status) || "No status"} · ${dateRange(g.startDate, g.endDate)}${b.dateProblem ? ` · ${b.dateProblem}: check the group's dates` : ""}`}>
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => onOpenGroup(g)}
                          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpenGroup(g)}
                          sx={{
                            position: "absolute", top: 4 + li * LANE_H + (LANE_H - PILL_H) / 2, height: PILL_H,
                            // A group with a bad end date is one night long; give it room for its name.
                            left: b.offset * dayW + 2, width: Math.max(b.span * dayW - 4, b.dateProblem ? 190 : 12),
                            borderRadius: PILL_H / 2, px: 1.25, cursor: "pointer", overflow: "hidden",
                            bgcolor: b.cancelled ? "transparent" : `${accent}1f`,
                            border: b.dateProblem ? "1.5px dashed #ed6c02" : `1px solid ${b.cancelled ? "#bdbdbd" : `${accent}66`}`,
                            opacity: b.cancelled ? 0.6 : 1,
                            display: "flex", alignItems: "center", gap: 1,
                            transition: "box-shadow 120ms, transform 120ms",
                            "&:hover, &:focus-visible": { boxShadow: 2, transform: "translateY(-1px)", outline: "none" },
                          }}
                        >
                          <Box sx={{ width: 10, height: 10, flex: "none", borderRadius: "50%", bgcolor: meta ? meta.color : "transparent", border: "1px solid rgba(0,0,0,0.3)" }} />
                          <Box sx={{ minWidth: 0, lineHeight: 1.15 }}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 600, textDecoration: b.cancelled ? "line-through" : "none" }}>
                              {g.groupName || `Group ${g.id}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap component="div">
                              {b.dateProblem
                                ? <Box component="span" sx={{ color: "warning.dark" }}>{b.dateProblem}</Box>
                                : <>{counts(g)} · {dateRange(g.startDate, g.endDate)}</>}
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}

                  {sec.indies ? (() => {
                    const span = indieSpan(sec.indies.daily);
                    const li = sec.bars.length;
                    const w = Math.max(span.length * dayW - 4, 12);
                    return (
                      <Tooltip title="Students not in a group. Click to list them with their dates.">
                        <Box
                          role="button"
                          tabIndex={0}
                          onClick={() => setIndiesFor(sec)}
                          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setIndiesFor(sec)}
                          sx={{
                            position: "absolute", top: 4 + li * LANE_H + (LANE_H - PILL_H) / 2, height: PILL_H,
                            left: span.start * dayW + 2, width: w,
                            borderRadius: PILL_H / 2, border: `1.5px dashed ${accent}`, cursor: "pointer", overflow: "hidden",
                            "&:hover, &:focus-visible": { boxShadow: 2, outline: "none" },
                          }}
                        >
                          <svg width={w} height={PILL_H} style={{ position: "absolute", left: 0, top: 0 }} aria-hidden="true">
                            <path d={sparklinePath(sec.indies.daily.slice(span.start, span.start + span.length), dayW, PILL_H - 4, sec.indies.peak)}
                              transform="translate(-2, 2)" fill={`${accent}33`} stroke={accent} strokeWidth="1" />
                          </svg>
                          <Typography variant="caption" noWrap sx={{ position: "relative", px: 1.5, lineHeight: `${PILL_H}px`, fontWeight: 600, fontStyle: "italic" }}>
                            Independent students · up to {sec.indies.peak}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })() : null}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {undated.length ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", pt: 1 }}>
          Not shown (no start date): {undated.map((g) => g.groupName || `Group ${g.id}`).join(", ")}
        </Typography>
      ) : null}

      <IndependentStudentsDialog
        open={Boolean(indiesFor)}
        centre={indiesFor ? { name: indiesFor.campus, indies: indiesFor.indies } : null}
        onClose={() => setIndiesFor(null)}
        onSelectStudent={onSelectStudent ? (id) => { setIndiesFor(null); onSelectStudent(id); } : undefined}
      />
    </Paper>
  );
}
